/**
 * TODO: 
 * - add onloadscreen
 */

import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import TranslucentShader from './CustomShader';
import pathToModel from '../../assets/models/garden.glb';
import texture1 from '../../assets/img/textures/caustic.png';
import texture2 from '../../assets/img/textures/bump.jpg';
import texture3 from '../../assets/img/textures/decal.png';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.startTime = Date.now();
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/031b26-cc2936-ebbab9-388697-b5ffe1');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[3], 50, 180);
        this.loader = new THREE.TextureLoader();

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.setupGlobalUniforms();
        this.setupPoints();
        this.setupGeometry();
    }

    setupCamera() {
        let VIEW_ANGLE = 70;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 10;
        let FAR_PLANE = 5000;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(0, 0, 1500);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    setupLight() {
        let light = new THREE.HemisphereLight(this.colourArr[2], this.colourArr[4], 1);
        light.position.set(0, 0.5, -1);
        this.scene.add(light);

        let dirLight = new THREE.DirectionalLight(this.colourArr[2], 3);
        this.scene.add(dirLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.autoRotateSpeed = .2;
        this.controls.autoRotate = true;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = false;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 5000;
        this.controls.minDistance = 1000;
        //this.controls.addEventListener('change', this.update.bind(this));
    };

    setupGlobalUniforms() {
        let texture = this.loader.load(texture3);
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        this.uniforms = {
            u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            u_time: { value: 0 },
            u_color0: { value: new THREE.Color(this.colourArr[2]) },
            u_color1: { value: new THREE.Color(this.colourArr[4]) },
            u_texture1: { value: texture }
        };
    }

    setupShaderOneMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec2 vUv; 
            
                void main()
                {
                    vNormal = normal;
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;

                varying vec2 vUv;
                varying vec3 vNormal;

                const float fluid_speed = 200.0;
                const float color_intensity = 0.5;

                float rand(vec2 co)
                {
                    return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
                }

                void main()
                {
                    vec2 pos = vUv * (gl_FragCoord.xy/u_resolution);

                    for(int i=1; i<45; i++)
                    {
                        vec2 newPos = pos + u_time * 0.02;
                        newPos.x+=0.6/float(i)*sin(float(i)*pos.y+u_time/fluid_speed+0.3*float(i)) + 0.5;
                        newPos.y+=0.6/float(i)*sin(float(i)*pos.x+u_time/fluid_speed+0.3*float(i+10)) - 0.5;
                        pos = newPos;
                    }

                    vec3 n = vNormal;
                    vec3 col = vec3(color_intensity * sin(0.5 * pos.x) + n.x, 
                                    color_intensity * sin(pos.x + pos.y),
                                    color_intensity * sin(1.0 * pos.y) + n.z);
                    gl_FragColor=vec4(col, 1.0);
                }
            `
        });
        return material;
    }

    setupShaderTwoMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec2 vUv; 
            
                void main()
                {
                    vNormal = normal;
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;

                varying vec2 vUv;
                varying vec3 vNormal;

                float rand(vec2 co)
                {
                    return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
                }

                float voronoi(vec2 pos) 
                {
                    vec2 p = floor(pos);
                    vec2 f = fract(pos);

                    float dist = 8.0; //dist = distance to closest feature point
                    for(int j=-1; j<=1; j++) 
                    {
                        for(int i=-1; i<=1; i++) 
                        {
                            vec2 lat = vec2(i, j); //lst = lattice point
                            vec2  r = vec2(lat) - f + rand(p + lat);
                            float d = dot(r, r); //d = current distance 
                            dist = min(dist, d);
                        }
                    }
                    return sqrt(dist);
                }

                void main()
                {
                    vec2 pos = vUv * 2.0 - 1.; //(gl_FragCoord.xy/u_resolution);
                    pos.x *= u_resolution.x / u_resolution.y;

                    float cell_size = sin(pos.y + u_time); //changing cells
                    float offset = voronoi(pos * cell_size - vec2(u_time * 0.5));
                    //main line: generates floating circles
                    float r = voronoi(pos + sin(pos.y + u_time));
                    float g = voronoi(pos) * cell_size + u_time;
                    
                    gl_FragColor = vec4(r*2.0, 1.-offset, 1.0 - r, 1.0);
                }
            `
        });
        return material;
    }

    setupTranslucentShader() {
        let imgTexture = this.loader.load(texture1);
        let thicknessTexture = this.loader.load(texture2);
        imgTexture.wrapS = imgTexture.wrapT = THREE.RepeatWrapping;

        let shader = TranslucentShader;
        let uniforms = THREE.UniformsUtils.clone(shader.uniforms);
        uniforms['map'].value = imgTexture;
        uniforms['diffuse'].value = new THREE.Vector3(1.0, 0.2, 0.2);
        uniforms['shininess'].value = 500;

        uniforms['thicknessMap'].value = thicknessTexture;
        uniforms['thicknessColor'].value = new THREE.Vector3(0.1, 0.3, 0.0);
        uniforms['thicknessDistortion'].value = 0.1;
        uniforms['thicknessAmbient'].value = 0.4;
        uniforms['thicknessAttenuation'].value = 0.8;
        uniforms['thicknessPower'].value = 2.0;
        uniforms['thicknessScale'].value = 16.0;

        let material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: shader.vertexShader,
            fragmentShader: shader.fragmentShader,
            lights: true
        });
        material.extensions.derivatives = true;
        return material;
    }

    loadModels() {
        let material = this.setupShaderOneMaterial();
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, (gltf) => {
            this.root = gltf.scene;
            this.root.position.set(10, -100, 50);
            this.root.traverse(node => {
                if (node.isMesh) {
                    node.material = material;
                    node.scale.x = 0.5;
                    node.scale.y = 0.5;
                    node.scale.z = 0.5;
                }
            });
            this.scene.add(this.root);
        });
    }

    setupGeometry() {
        let material = this.setupShaderOneMaterial();
        let translMaterial = this.setupTranslucentShader();
        let meshArray = [];
        let sphereGeo = new THREE.SphereBufferGeometry(100, 32, 32);
        let group = new THREE.Mesh(sphereGeo, translMaterial);
        this.scene.add(group);

        let icoGeometry = new THREE.IcosahedronGeometry(90, 1);
        let cylGeometry = new THREE.CylinderGeometry(1, 20, 1000, 3, 40);
        cylGeometry.applyMatrix4(new THREE.Matrix4().setPosition(new THREE.Vector3(0, 400, 0)));

        for (let i = 0; i < cylGeometry.vertices.length - 3; i += 3) {
            //create organic-like spirals
            let x = Math.sin(i * 0.09) * 40;
            let z = Math.cos(i * 0.07) * 40;

            //change the values below to shape vertices 
            cylGeometry.vertices[i].x += x;
            cylGeometry.vertices[i].z += z;
            cylGeometry.vertices[i + 1].x += x;
            cylGeometry.vertices[i + 1].z += z;
            cylGeometry.vertices[i + 2].x += x;
            cylGeometry.vertices[i + 2].z += z;
        }

        cylGeometry.applyMatrix4(new THREE.Matrix4()
            .makeRotationFromEuler(new THREE.Euler(-Math.PI * 0.5, 0, 0)));

        for (let i = 0; i < icoGeometry.vertices.length; i++) {
            let m = new THREE.Mesh(cylGeometry, material);
            m.position.set(icoGeometry.vertices[i].x, icoGeometry.vertices[i].y, icoGeometry.vertices[i].z);
            m.lookAt(this.scene.position);
            m.rotation.z = Math.random() * Math.PI * 2;
            meshArray.push(m);
            group.add(m);
        }
    }

    setupPoints() {
        const radius = 150;
        const segments = 68;
        const rings = 38;

        let vertices = new THREE.SphereGeometry(radius, segments, rings).vertices;
        let length1 = vertices.length;
        let positions = new Float32Array(vertices.length * 3);
        let colors = new Float32Array(vertices.length * 3);
        let sizes = new Float32Array(vertices.length);

        let vertex;
        let color = new THREE.Color();

        for (let i = 0; i < vertices.length; i++) {
            vertex = vertices[i];
            vertex.toArray(positions, i * 3);
            if (i < length1) {
                color.setHSL(0.01 + 0.1 * (i / length1), 0.99, (vertex.y + radius) / (4 * radius));
            }
            else {
                color.setHSL(0.6, 0.75, 0.25 + vertex.y / (2 * radius));
            }

            color.toArray(colors, i * 3);
            sizes[i] = i < length1 ? 10 : 40;
        }

        let geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec3 vColor;
                
                attribute float size;
                attribute vec3 color;
                
                void main()
                {
                    vColor = color;
                
                    vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                    gl_PointSize = size * ( 300.0 / -mvPosition.z );
                    gl_Position = projectionMatrix * mvPosition;
                }
          `,
            fragmentShader: `
            varying vec3 vColor;

            uniform float u_time;
            uniform vec2 u_resolution;
            uniform vec3 u_color0;
            uniform sampler2D u_texture1;

            void main()
            {
                vec4 color = vec4(u_color0 * vColor, 1.0) * texture2D(u_texture1, gl_PointCoord);
                gl_FragColor = color;
            }
          `,
            transparent: true
        });

        let sphere = new THREE.Points(geometry, material);
        this.scene.add(sphere);

        this.sphere = sphere;
        this.length1 = length1;
    }

    sortPoints = function () {
        let vector = new THREE.Vector3();
        // Model View Projection matrix
        let matrix = new THREE.Matrix4();
        matrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
        matrix.multiply(this.sphere.matrixWorld);

        let geometry = this.sphere.geometry;
        //console.log(geometry);
        let index = geometry.getIndex();
        let positions = geometry.getAttribute('position').array;
        let length = positions.length / 3;
        if (index === null) {
            let array = new Uint16Array(length);
            for (let i = 0; i < length; i++) {
                array[i] = i;
            }
            index = new THREE.BufferAttribute(array, 1);
            geometry.setIndex(index);
        }

        let sortArray = [];
        for (let i = 0; i < length; i++) {
            vector.fromArray(positions, i * 3);
            vector.applyMatrix4(matrix);
            sortArray.push([vector.z, i]);
        }

        function numericalSort(a, b) {
            return b[0] - a[0];
        }

        sortArray.sort(numericalSort);

        let indices = index.array;
        for (let i = 0; i < length; i++) {
            indices[i] = sortArray[i][1];
        }
        geometry.index.needsUpdate = true;
    }

    animatePoints(time) {
        this.sphere.rotation.y = 0.02 * time;
        this.sphere.rotation.z = 0.02 * time;
        let geometry = this.sphere.geometry;
        let attributes = geometry.attributes;
        for (let i = 0; i < attributes.size.array.length; i++) {
            if (i < this.length1) {
                attributes.size.array[i] = 16 + 12 * Math.sin(0.1 * i + time);
            }
        }
        attributes.size.needsUpdate = true;
    }

    raycast() {
        this.raycaster.setFromCamera(this.mouse, this.camera);
        let intersects = this.raycaster.intersectObjects(this.scene.children, true);
        if (intersects.length > 0) {
            intersects[0].object.material = this.setupShaderTwoMaterial();
        }
    }

    orbitCamera(rotSpeed) {
        let x = this.camera.position.x;
        let z = this.camera.position.z;

        this.camera.position.x = x * Math.cos(rotSpeed) + z * Math.sin(rotSpeed);
        this.camera.position.z = z * Math.cos(rotSpeed) - x * Math.sin(rotSpeed);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    updateSceneSubjects(time) {
        this.animatePoints(time);
        this.sortPoints();
    }

    update() {
        if (this.disposed) return;
        // if(this.camera) this.orbitCamera(0.001);
        if (this.controls) this.controls.update();
        if (this.uniforms) this.uniforms['u_time'].value = 0.0005 * (Date.now() - this.startTime);
        this.updateSceneSubjects(Date.now() * 0.005);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onClick(e) {
        this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = - (e.clientY / window.innerHeight) * 2 + 1;
        this.raycast();
        this.update();
    }

    dispose() {
        this.disposed = true;
        this.scene.dispose();
        this.renderer.dispose();
    }
}